import { ROUTES } from '@/constants/routes';
import {
  canEditOrganization,
  isAdmin,
  isInAgencyScope,
  isManagedClient,
  isReadOnlyOrg,
} from '@/lib/roles';
import type { CurrentUserResponse } from '@/types/generated';

const PUBLIC_PATHS: ReadonlySet<string> = new Set([
  ROUTES.HOME,
  ROUTES.LOGIN,
  ROUTES.SIGNUP,
  ROUTES.VERIFY_EMAIL,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.RESET_PASSWORD,
]);

type RedirectTarget =
  | typeof ROUTES.LOGIN
  | typeof ROUTES.SIGNUP
  | typeof ROUTES.VERIFY_EMAIL
  | typeof ROUTES.ORGANIZATION_SETUP
  | typeof ROUTES.CONNECT_PLATFORMS
  | typeof ROUTES.PAYWALL
  | typeof ROUTES.DASHBOARD
  | AgencyTarget;

type AgencyTarget =
  | typeof ROUTES.AGENCY_PORTFOLIO
  | typeof ROUTES.AGENCY_SETUP
  | typeof ROUTES.AGENCY_TEAM
  | typeof ROUTES.AGENCY_CONNECTIONS
  | typeof ROUTES.AGENCY_BILLING
  | typeof ROUTES.AGENCY_SETTINGS
  | typeof ROUTES.AGENCY_SETTINGS_NOTIFICATIONS
  | typeof ROUTES.AGENCY_SETTINGS_SECURITY;

interface RedirectContext {
  pathname: string;
  user: CurrentUserResponse | null | undefined;
  paywallSeen?: boolean;
}

const pinTo = (pathname: string, target: string): RedirectTarget | null =>
  pathname === target ? null : (target as RedirectTarget);

export const orgDetailsCompleted = (
  user: CurrentUserResponse | null | undefined,
): boolean => {
  const org = user?.organization;
  if (!org) return false;
  return Boolean(org.industry && org.organization_region && org.timezone);
};

const orgOnboardingCompleted = (
  user: CurrentUserResponse | null | undefined,
): boolean => Boolean(user?.organization?.onboarding_completed);

const ONBOARDING_FLOW_PATHS: ReadonlySet<string> = new Set([
  ROUTES.ORGANIZATION_SETUP,
  ROUTES.CONNECT_PLATFORMS,
]);

// Reachable by a READ_ONLY admin: the paywall itself, plus the surfaces that let them pay or extend the trial.
const PAYWALL_EXEMPT_PATHS: ReadonlySet<string> = new Set([
  ROUTES.PAYWALL,
  ROUTES.SETTINGS_BILLING,
  ROUTES.AGENCY_BILLING,
  ROUTES.BILLING_RETURN,
  ROUTES.EXTEND_TRIAL,
]);

// The pages an agency tab shares with advertisers; every other advertiser page is refused.
const AGENCY_SHARED_PATHS: ReadonlySet<string> = new Set([
  ROUTES.SUPPORT,
  ROUTES.NOTIFICATIONS,
  ROUTES.PAYWALL,
  ROUTES.BILLING_RETURN,
  ROUTES.EXTEND_TRIAL,
]);

// An advertiser link that has an agency equivalent lands there instead of on the portfolio.
const AGENCY_TWINS: Readonly<Record<string, AgencyTarget>> = {
  '/settings': ROUTES.AGENCY_SETTINGS,
  '/settings/team': ROUTES.AGENCY_TEAM,
  [ROUTES.SETTINGS_BILLING]: ROUTES.AGENCY_BILLING,
  '/settings/notifications': ROUTES.AGENCY_SETTINGS_NOTIFICATIONS,
  '/settings/security': ROUTES.AGENCY_SETTINGS_SECURITY,
  '/settings/connections': ROUTES.AGENCY_CONNECTIONS,
  [ROUTES.CONNECTORS]: ROUTES.AGENCY_CONNECTIONS,
};

const isAgencyPath = (pathname: string): boolean =>
  pathname === ROUTES.AGENCY_PORTFOLIO ||
  pathname.startsWith(`${ROUTES.AGENCY_PORTFOLIO}/`);

export const homePathFor = (
  user: CurrentUserResponse | null | undefined,
): typeof ROUTES.AGENCY_PORTFOLIO | typeof ROUTES.DASHBOARD =>
  isInAgencyScope(user) ? ROUTES.AGENCY_PORTFOLIO : ROUTES.DASHBOARD;

const resolveAdminMidOnboarding = (
  pathname: string,
  user: CurrentUserResponse,
): RedirectTarget | null => {
  // The emailed extension also reaches an admin who stopped mid-onboarding.
  if (pathname === ROUTES.EXTEND_TRIAL) return null;
  if (ONBOARDING_FLOW_PATHS.has(pathname)) {
    // /connect-platforms needs step 1's data; send back if it's missing.
    if (pathname !== ROUTES.ORGANIZATION_SETUP && !orgDetailsCompleted(user)) {
      return ROUTES.ORGANIZATION_SETUP;
    }
    return null;
  }
  return orgDetailsCompleted(user)
    ? ROUTES.CONNECT_PLATFORMS
    : ROUTES.ORGANIZATION_SETUP;
};

// Reached only for admins; also denies /paywall to an org that isn't READ_ONLY or can't pay itself.
const resolvePaywall = (
  pathname: string,
  user: CurrentUserResponse,
  paywallSeen: boolean,
): RedirectTarget | null => {
  const canPay = isReadOnlyOrg(user) && !isManagedClient(user);
  if (pathname === ROUTES.PAYWALL) return canPay ? null : homePathFor(user);
  if (paywallSeen || !canPay) return null;
  return PAYWALL_EXEMPT_PATHS.has(pathname) ? null : ROUTES.PAYWALL;
};

// Only an admin can submit it, and the API refuses it to a READ_ONLY agency.
const needsAgencySetup = (user: CurrentUserResponse): boolean =>
  canEditOrganization(user) && !orgOnboardingCompleted(user);

// An agency's one onboarding step is its own setup; it keeps the paywall and its own pages, nothing else.
const resolveAgencyScope = (
  pathname: string,
  user: CurrentUserResponse,
  paywallSeen: boolean,
): RedirectTarget | null => {
  if (isAdmin(user)) {
    const paywall = resolvePaywall(pathname, user, paywallSeen);
    if (paywall) return paywall;
  } else if (pathname === ROUTES.PAYWALL) {
    return ROUTES.AGENCY_PORTFOLIO;
  }
  if (needsAgencySetup(user)) return pinTo(pathname, ROUTES.AGENCY_SETUP);
  if (pathname === ROUTES.AGENCY_SETUP) return ROUTES.AGENCY_PORTFOLIO;
  if (isAgencyPath(pathname) || AGENCY_SHARED_PATHS.has(pathname)) return null;
  return AGENCY_TWINS[pathname] ?? ROUTES.AGENCY_PORTFOLIO;
};

const resolveAdvertiserScope = (
  pathname: string,
  user: CurrentUserResponse,
  paywallSeen: boolean,
): RedirectTarget | null => {
  const onboardingPath = ONBOARDING_FLOW_PATHS.has(pathname);

  // Non-admins never see the onboarding wizard or the paywall; bounce them to the dashboard.
  if (!isAdmin(user)) {
    if (onboardingPath || pathname === ROUTES.PAYWALL) return ROUTES.DASHBOARD;
    return PUBLIC_PATHS.has(pathname) ? ROUTES.DASHBOARD : null;
  }

  // Admin mid-onboarding: wizard paths are open, everything else funnels back to the current step.
  if (!orgOnboardingCompleted(user)) {
    return resolveAdminMidOnboarding(pathname, user);
  }

  // Onboarded admin revisiting the wizard — done is done, Settings is the surface now.
  if (onboardingPath) return ROUTES.DASHBOARD;

  const paywall = resolvePaywall(pathname, user, paywallSeen);
  if (paywall) return paywall;

  if (PUBLIC_PATHS.has(pathname)) return ROUTES.DASHBOARD;
  return null;
};

const resolveVerifiedUser = (
  pathname: string,
  user: CurrentUserResponse,
  paywallSeen: boolean,
): RedirectTarget | null => {
  if (isInAgencyScope(user)) {
    return resolveAgencyScope(pathname, user, paywallSeen);
  }
  const target = resolveAdvertiserScope(pathname, user, paywallSeen);
  if (target) return target;
  return isAgencyPath(pathname) ? ROUTES.DASHBOARD : null;
};

// Single source of truth for post-auth routing; returns the redirect target or null.
export const getRedirectPath = ({
  pathname,
  user,
  paywallSeen = false,
}: RedirectContext): RedirectTarget | null => {
  if (!user) {
    return PUBLIC_PATHS.has(pathname) ? null : ROUTES.LOGIN;
  }

  if (!user.is_verified) {
    return pinTo(pathname, ROUTES.VERIFY_EMAIL);
  }

  return resolveVerifiedUser(pathname, user, paywallSeen);
};
